import React from 'react';
import {Telemetry} from '@packages/systems/telemetry';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import type {
  App,
  NotificationATCContextValue,
  NotificationATCEntry,
} from './types';
import {isCollisionAllowed, getCollisionTelemetryPayload} from './utils';

const handleCollision = (
  app: App,
  collisionTimestamp: string,
  incomingIpn: NotificationATCEntry,
  existingIpn: NotificationATCEntry,
  telemetry: Telemetry<unknown> | null
) => {
  const isAllowedCollision = isCollisionAllowed(app, incomingIpn, existingIpn);
  const collisionPayload = {
    ...getCollisionTelemetryPayload(incomingIpn, existingIpn),
    collision_timestamp: collisionTimestamp,
    is_collision_allowed: isAllowedCollision,
  };
  if (
    !isAllowedCollision &&
    getFeatureFlagOutsideOfReact(
      FEATURE_FLAGS.NOTIFICATION_ATC_SEND_COLLISION_EVENT
    )
  ) {
    telemetry?.sendEvent('IPN_COLLISION', collisionPayload);
  }
};

export const NotificationATCContext =
  React.createContext<NotificationATCContextValue | null>(null);

export const NotificationATCProvider: React.FC<{
  app: 'dashboard' | 'designer' | 'marketplace';
  children: React.ReactNode;
  telemetry: Telemetry<unknown> | null;
}> = ({app, children, telemetry}) => {
  const [_, setNotifications] = React.useState<NotificationATCEntry[]>([]);

  // Register should be stable except when the app or telemetry changes.
  const register = React.useCallback(
    (incomingNotification: NotificationATCEntry) => {
      const now = new Date().toISOString();
      setNotifications((existingNotifications: NotificationATCEntry[]) => {
        // If incoming notification already exists, then don't register it again.
        // This is to guard against scenarios where the notification doesn't implement `unregister` correctly.
        if (
          existingNotifications.some(
            (notification) => notification.id === incomingNotification.id
          )
        ) {
          return existingNotifications;
        }
        existingNotifications.forEach((notification) => {
          handleCollision(
            app,
            now,
            incomingNotification,
            notification,
            telemetry
          );
        });
        return [...existingNotifications, incomingNotification];
      });
    },
    [app, telemetry]
  );

  // Unregister should always be stable.
  const unregister = React.useCallback((incomingNotificationId: string) => {
    setNotifications((existingNotifications: NotificationATCEntry[]) => {
      return existingNotifications.filter(
        (notification) => notification.id !== incomingNotificationId
      );
    });
  }, []);

  // NotificationATCContext should be stable except when app and telemetry change.
  const value = React.useMemo(
    () => ({register, unregister}),
    [register, unregister]
  );

  return (
    <NotificationATCContext.Provider value={value}>
      {children}
    </NotificationATCContext.Provider>
  );
};
